import React from 'react';
import { useTranslation } from 'react-i18next';
import { SivaContractDetailDto, SivaContractUpdateDto, SivaPhase } from 'src/api/v2';
import useAdvisorOptions from '../../hooks/use-advisor-options';
import useCompanyContract from '../../company-details/hooks/use-company-contract';
import useToggle from 'src/hooks/use-toggle';
import { Controller, useForm } from 'react-hook-form';
import { SelectWrapper } from 'src/components/Form/SelectWrapper';
import { requiredI18n } from 'src/utils/validation';
import { bransjeOptions } from '../../constants';
import { SelectOptionProps, TextArea } from '@innovationnorway/component-library';
import { Link } from 'react-router-dom';
import ConnectCompany from '../../company-details/components/ConnectCompany';
import { Autocomplete } from 'src/components/Autocomplete/Autocomplete';
import { Button, Heading, Icon } from '@innovationnorway/cl-react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';

type Props = {
  contract: SivaContractDetailDto;
  selectedPhase: SivaPhase;
  onCancel: () => void;
};
const PreIncWithoutCompanyForm: React.FC<Props> = ({ contract, selectedPhase, onCancel }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tSiva } = useTranslation('siva');
  const { t: tError } = useTranslation('error');
  const [companyError, setCompanyError] = React.useState<boolean>(false);
  const [orgNumber, setOrgNumber] = React.useState<string>('');
  const [companyName, setCompanyName] = React.useState<string>('');
  const { advisorOptions } = useAdvisorOptions();
  const { currentPhase, updateSivaContractMutation } = useCompanyContract(contract.numericId);
  const [updateError, setUpdateError] = React.useState(false);
  const { activate, deactivate, isActive } = useToggle();
  const { register, handleSubmit, control } = useForm<SivaContractUpdateDto>({
    defaultValues: {
      ...contract,
      newPhase: {
        newPhase: undefined,
      },
      advisorId: contract?.advisorId,
    },
  });
  const onSave = handleSubmit(async (formData) => {
    updateSivaContractMutation
      .mutateAsync({
        ...formData,
        newPhase: {
          newPhase: selectedPhase,
          fromDate: new Date(),
        },
        organizationNumber: orgNumber,
        externalIndustry: formData.externalIndustry,
      })
      .then(() => {
        setUpdateError(false);
      })
      .catch(() => {
        setUpdateError(true);
      })
      .finally(() => {
        activate();
      });
  });
  return (
    <form onSubmit={onSave}>
      <Heading level={2}>Avtaleinformasjon</Heading>
      <p>Ny avtale vil opprettes og påkrevde felter må fylles ut</p>

      {currentPhase && (
        <ConnectCompany
          phase={selectedPhase}
          orgNumber={orgNumber}
          setOrgNumber={setOrgNumber}
          selectedCompanyName={companyName}
          setSelectedCompanyName={setCompanyName}
          setCompanyError={setCompanyError}
          showNoCompanyCheckbox={false}
        />
      )}

      <SelectWrapper
        {...register('externalIndustry', {
          required: requiredI18n(tError),
        })}
        label="Velg bransje"
        options={bransjeOptions}
      />

      <Heading level={2}>Oppfølging</Heading>
      <Controller
        control={control}
        name="advisorId"
        render={({ field }) => (
          <Autocomplete<SelectOptionProps>
            label={tCommon('advisor')}
            name={field.name}
            items={advisorOptions}
            filter={(item, value: string): boolean => {
              const lowerCaseValue = value.toLowerCase();
              return item.text.toLowerCase().includes(lowerCaseValue) || false;
            }}
            onItemSelected={(advisor) => field.onChange(advisor.value)}
            itemLabel={(advisor) => `${advisor.text}`}
            renderItem={(advisor) => `${advisor.text}`}
            defaultValue={advisorOptions.find((x) => x.value === field.value)?.text}
          />
        )}
      />

      <p className="margin-top--0 margin-bottom--4">
        {tSiva('newCompany.contract.form.advisorId.link.text')}{' '}
        <Link to={`/actor-profile/settings/access`} target="_blank">
          <span className="margin-right--1">{tCommon('accessControl')}</span>
          <Icon prefix="fasr" iconName="external-link" />
        </Link>
      </p>

      <TextArea label="Notat" value={contract.notes} name="notes" />

      <div className="display--flex gap--1 justify-content--flex-start margin-top--7">
        <Button type="submit" disabled={companyError}>
          {tCommon('save')}
        </Button>
        <Button variant="secondary" type="button" onClick={onCancel}>
          {tCommon('cancel')}
        </Button>

        <DialogContainer
          show={isActive}
          onClose={updateError ? deactivate : onCancel}
          title={updateError ? tSiva('changePhase.error.title') : tCommon('confirmation')}
        >
          {updateError ? (
            <>
              <p>{tSiva('changePhase.error.text')}.</p>
              <Button type="button" onClick={deactivate}>
                {tCommon('close')}
              </Button>
            </>
          ) : (
            <>
              <p>{`${tSiva('changePhase.success.text')} ${tSiva(`phase.${currentPhase?.sivaPhase}` as any)},
              ${tCommon('to')} ${tSiva(`phase.${selectedPhase}` as any)}`}</p>
              <Button type="button" onClick={onCancel}>
                {tCommon('close')}
              </Button>
            </>
          )}
        </DialogContainer>
      </div>
    </form>
  );
};
export default PreIncWithoutCompanyForm;
